import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Presentation } from 'lucide-react';
import { VENUE_LABELS, addDays, addMinutes, can, weekStart, type Game, type PlayerAvailability, type SessionSummary, type TeamSummary } from '@hoopify/shared';
import { gameResult, RESULT_TONE } from '../lib/games';
import { api, errorMessage } from '../lib/api';
import { formatDayNum, formatLongDay, formatMinutes, formatTime, formatWeekday, today, weekLabel } from '../lib/dates';
import { Alert, Button, Empty, LoadingBlock, PageHeader } from '../components/ui';
import { MyJoinRequests } from '../components/join/JoinTeam';
import { AnswerRow, AwayButton, AwayList, OutChip, useAwayCounts, useMyAvailability, useMyPlayers } from '../components/availability/Availability';
import { NewSessionDialog } from '../components/sessions/NewSessionDialog';
import { StatusBadge } from '../components/sessions/SessionStatus';
import { teamDotStyle } from '../components/sessions/teamColour';

export function SchedulePage() {
  const [params, setParams] = useSearchParams();
  const thisWeek = weekStart(today());
  const start = /^\d{4}-\d{2}-\d{2}$/.test(params.get('week') ?? '') ? weekStart(params.get('week')!) : thisWeek;
  const end = addDays(start, 6);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  const [creating, setCreating] = useState<{ date?: string } | null>(null);

  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });
  const sessions = useQuery({
    queryKey: ['schedule', start],
    queryFn: () => api.get<SessionSummary[]>(`/schedule?from=${start}&to=${end}`),
    placeholderData: (prev) => prev,
  });
  const games = useQuery({
    queryKey: ['schedule-games', start],
    queryFn: () => api.get<Game[]>(`/schedule/games?from=${start}&to=${end}`),
    placeholderData: (prev) => prev,
  });
  const gamesOn = (d: string) => (games.data ?? []).filter((g) => g.date === d);
  const canPlan = (teams.data ?? []).some((t) => can(t.role, 'sessions:write'));
  const coachSomewhere = (teams.data ?? []).some((t) => can(t.role, 'sessions:details'));
  const myPlayers = useMyPlayers();
  const hasPlayers = (myPlayers.data?.length ?? 0) > 0;
  const mine = useMyAvailability(start, end, hasPlayers);
  const out = useAwayCounts(start, end, coachSomewhere);
  const gameToday = gamesOn(today()).length > 0;
  const go = (week: string) => setParams(week === thisWeek ? {} : { week }, { replace: true });
  const byDay = (d: string) => (sessions.data ?? []).filter((s) => s.date === d);
  const multiTeam = new Set((sessions.data ?? []).map((s) => s.teamId)).size > 1 || (teams.data?.length ?? 0) > 1;

  return (
    <>
      <PageHeader
        title="Schedule"
        description="Training sessions across all your teams."
        actions={
          (canPlan || hasPlayers) && (
            <>
              {hasPlayers && <AwayButton players={myPlayers.data!} />}
              {canPlan && gameToday && (
                <Link to="/gameday" className="btn-secondary" title="Open the court whiteboard">
                  <Presentation className="size-4" aria-hidden /> Game day
                </Link>
              )}
              {canPlan && (
                <Button onClick={() => setCreating({})}>
                  <Plus className="size-4" aria-hidden /> New session
                </Button>
              )}
            </>
          )
        }
      />
      <MyJoinRequests />
      {hasPlayers && <AwayList />}
      {myPlayers.data && (teams.data ?? []).some((t) => (t.role === 'player' || t.role === 'parent') && !myPlayers.data!.some((p) => p.teamId === t.id)) && (
        <div className="mb-5">
          <Alert tone="info">
            To say when you can’t make a session, ask the coach of {(teams.data ?? []).filter((t) => (t.role === 'player' || t.role === 'parent') && !myPlayers.data!.some((p) => p.teamId === t.id)).map((t) => t.name).join(' and ')} to link you to a player on the roster.
          </Alert>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="tnum text-[17px] font-semibold">{weekLabel(start, end)}</h2>
        <div className="flex items-center gap-1">
          <button className="btn-secondary px-2.5" onClick={() => go(addDays(start, -7))} aria-label="Previous week">
            <ChevronLeft className="size-4" />
          </button>
          <button className="btn-secondary" onClick={() => go(thisWeek)} disabled={start === thisWeek}>
            This week
          </button>
          <button className="btn-secondary px-2.5" onClick={() => go(addDays(start, 7))} aria-label="Next week">
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      {teams.isLoading || (sessions.isLoading && !sessions.data) ? (
        <LoadingBlock />
      ) : sessions.isError ? (
        <Alert>{errorMessage(sessions.error)}</Alert>
      ) : !teams.data?.length ? (
        <Empty title="You’re not on a team yet" action={<Link to="/teams" className="btn-primary">Go to Teams</Link>}>
          Create a team, or ask your coach to invite you. Sessions show up here once there’s a team.
        </Empty>
      ) : (
        <>
          {/* Laptop: the week as seven columns */}
          <div className="hidden overflow-hidden rounded-lg border border-line bg-surface md:grid md:grid-cols-7 md:divide-x md:divide-line">
            {days.map((d) => {
              const isToday = d === today();
              const list = byDay(d);
              return (
                <section key={d} className={`group/day flex min-h-[220px] min-w-0 flex-col ${isToday ? 'bg-action-soft/40' : ''}`} aria-label={formatLongDay(d)}>
                  <header className="flex items-center justify-between border-b border-line px-2.5 py-2">
                    <p className="text-[13px]">
                      <span className="text-muted">{formatWeekday(d)}</span>{' '}
                      <span className={`tnum font-semibold ${isToday ? 'rounded bg-action px-1 text-action-ink' : ''}`}>{formatDayNum(d)}</span>
                    </p>
                    {canPlan && (
                      <button
                        className="rounded p-0.5 text-faint [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/day:opacity-100 hover:bg-sunken hover:text-ink focus:opacity-100"
                        onClick={() => setCreating({ date: d })}
                        aria-label={`New session on ${formatLongDay(d)}`}
                      >
                        <Plus className="size-4" />
                      </button>
                    )}
                  </header>
                  <ol className="flex-1 space-y-1.5 p-1.5">
                    {gamesOn(d).map((g) => (
                      <li key={g.id}>
                        <GameCard g={g} showTeam={multiTeam} compact mine={mine.data?.games[g.id]} out={out.data?.games[g.id]} />
                      </li>
                    ))}
                    {list.map((s) => (
                      <li key={s.id}>
                        <SessionCard s={s} showTeam={multiTeam} compact mine={mine.data?.sessions[s.id]} out={out.data?.sessions[s.id]} />
                      </li>
                    ))}
                  </ol>
                </section>
              );
            })}
          </div>

          {/* Phone: a list by day */}
          <div className="space-y-5 md:hidden">
            {(sessions.data ?? []).length === 0 && !(games.data ?? []).length && (
              <Empty title="Nothing this week" action={canPlan ? <Button onClick={() => setCreating({})}>New session</Button> : undefined}>
                {canPlan ? 'Plan a session, or check another week.' : 'Your coach hasn’t published any sessions for this week.'}
              </Empty>
            )}
            {days
              .filter((d) => byDay(d).length || gamesOn(d).length)
              .map((d) => (
                <section key={d}>
                  <h3 className={`mb-2 text-[14px] font-semibold ${d === today() ? 'text-action' : ''}`}>
                    {formatLongDay(d)}
                    {d === today() ? ' (today)' : ''}
                  </h3>
                  <ol className="space-y-2">
                    {gamesOn(d).map((g) => (
                      <li key={g.id}>
                        <GameCard g={g} showTeam={multiTeam} mine={mine.data?.games[g.id]} out={out.data?.games[g.id]} />
                      </li>
                    ))}
                    {byDay(d).map((s) => (
                      <li key={s.id}>
                        <SessionCard s={s} showTeam={multiTeam} mine={mine.data?.sessions[s.id]} out={out.data?.sessions[s.id]} />
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
          </div>
          {(sessions.data ?? []).length === 0 && !(games.data ?? []).length && (
            <p className="mt-4 hidden text-[14px] text-muted md:block">
              {canPlan ? 'Nothing planned this week. Use New session, or the + on any day.' : 'Your coach hasn’t published any sessions for this week.'}
            </p>
          )}
        </>
      )}

      <NewSessionDialog open={!!creating} onClose={() => setCreating(null)} date={creating?.date} />
    </>
  );
}

type Out = { name: string; reason: string | null }[];

function SessionCard({ s, showTeam, compact, mine, out }: { s: SessionSummary; showTeam: boolean; compact?: boolean; mine?: PlayerAvailability[]; out?: Out }) {
  const end = addMinutes(s.startTime, s.durationMinutes);
  return (
    <div className={`rounded-md border bg-surface hover:border-action/60 ${s.status === 'draft' ? 'border-dashed border-line-strong' : 'border-line'} ${compact ? 'px-2 py-1.5' : 'px-3.5 py-3'}`}>
    <Link to={`/teams/${s.teamId}/sessions/${s.id}`} className="block">
      <p className={`tnum text-muted ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
        {formatTime(s.startTime)}
        {!compact && ` – ${formatTime(end)}`}
      </p>
      <p className={`leading-snug font-medium ${compact ? 'text-[13px]' : ''}`}>{s.title}</p>
      {showTeam && (
        <p className={`mt-0.5 flex items-center gap-1.5 text-muted ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
          <span className="size-2 shrink-0 rounded-full" style={teamDotStyle(s.teamId)} aria-hidden />
          <span className="truncate">{s.teamName}</span>
        </p>
      )}
      <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 ${compact ? 'text-[11px]' : 'text-[12px]'} text-muted`}>
        <StatusBadge status={s.status} />
        {!compact && s.location && <span>{s.location}</span>}
        {s.drillCount > 0 && <span className="tnum">{s.plannedMinutes === s.durationMinutes || compact ? `${s.drillCount} ${s.drillCount === 1 ? "drill" : "drills"}` : `${formatMinutes(s.plannedMinutes)} of ${formatMinutes(s.durationMinutes)} planned`}</span>}
        <OutChip list={out} compact={compact} />
      </div>
    </Link>
    {mine && mine.length > 0 && <AnswerRow kind="session" teamId={s.teamId} itemId={s.id} title={s.title} players={mine} compact={compact} />}
    </div>
  );
}

function GameCard({ g, showTeam, compact, mine, out }: { g: Game; showTeam: boolean; compact?: boolean; mine?: PlayerAvailability[]; out?: Out }) {
  return (
    <div className={`rounded-md border-l-4 border-action bg-action-soft ${compact ? 'px-2 py-1.5' : 'px-3.5 py-3'}`} title={g.notes ?? undefined}>
      <p className={`tnum text-muted ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
        Game{g.startTime ? `, ${formatTime(g.startTime)}` : ''}
      </p>
      <p className={`leading-snug font-semibold ${compact ? 'text-[13px]' : ''}`}>vs {g.opponent}</p>
      {(() => {
        const r = gameResult(g);
        return r ? <p className={`font-semibold ${RESULT_TONE[r.tone]} ${compact ? 'text-[12px]' : 'text-[13px]'}`}>{r.text}</p> : null;
      })()}
      <p className={`text-muted ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
        {VENUE_LABELS[g.venue]}
        {!compact && g.location ? `, ${g.location}` : ''}
      </p>
      {showTeam && (
        <p className={`mt-0.5 flex items-center gap-1.5 text-muted ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
          <span className="size-2 shrink-0 rounded-full" style={teamDotStyle(g.teamId)} aria-hidden />
          <span className="truncate">{g.teamName}</span>
        </p>
      )}
      {out && out.length > 0 && (
        <div className="mt-1">
          <OutChip list={out} compact={compact} />
        </div>
      )}
      {mine && mine.length > 0 && <AnswerRow kind="game" teamId={g.teamId} itemId={g.id} title={`the game against ${g.opponent}`} players={mine} compact={compact} />}
    </div>
  );
}

export { SessionCard };
